import { awards } from '../../data/awards'

/**
 * Generate network node positions for the Proof constellation.
 * Each award becomes a node. Particles are assigned to their nearest node,
 * creating clusters that form the network graph.
 *
 * Returns Float32Array of [x, y, z] per particle (same length as PARTICLE_COUNT * 3).
 */

interface NetworkNode {
  id: number
  x: number  // normalized -1..1
  y: number  // normalized -1..1
}

/**
 * Generate deterministic node positions in a force-directed-ish layout.
 * Uses a golden-angle spiral to evenly distribute nodes, then adds
 * category-based clustering.
 */
function generateNodePositions(): NetworkNode[] {
  const n = awards.length
  const nodes: NetworkNode[] = []

  // Golden angle spiral — tighter cluster toward center
  const goldenAngle = Math.PI * (3 - Math.sqrt(5))

  for (let i = 0; i < n; i++) {
    const r = 0.5 + 0.66 * Math.sqrt(i / n) // stays well within 0..0.46
    const theta = i * goldenAngle

    nodes.push({
      id: awards[i].id,
      x: r * Math.cos(theta) * 1.6,  // slight horizontal stretch
      y: r * Math.sin(theta) * 0.6,  // nearly circular
    })
  }

  return nodes
}

/**
 * Build per-particle network target positions.
 * Only a handful of particles sit at each node center (z = 0.01 flag).
 * The rest are ambient dust (z = 0.0) and rendered invisible by the shader.
 */
export function buildNetworkTargets(particleCount: number): {
  positions: Float32Array
  nodePositions: NetworkNode[]
} {
  const nodes = generateNodePositions()
  const positions = new Float32Array(particleCount * 3)

  // Exactly 1 particle per node for a clean single-dot
  let idx = 0

  for (let n = 0; n < nodes.length && idx < particleCount; n++) {
    const node = nodes[n]
    positions[idx * 3]     = node.x
    positions[idx * 3 + 1] = node.y
    positions[idx * 3 + 2] = 0.01  // z flag → node particle
    idx++
  }

  // Remaining: scattered ambient dust (invisible via shader, z = 0)
  for (; idx < particleCount; idx++) {
    const s1 = Math.sin(idx * 12.9898 + 78.233) * 43758.5453 % 1
    const s2 = Math.sin(idx * 45.164 + 29.673) * 43758.5453 % 1
    positions[idx * 3]     = (Math.abs(s1) * 2 - 1) * 1.3
    positions[idx * 3 + 1] = (Math.abs(s2) * 2 - 1) * 0.8
    positions[idx * 3 + 2] = 0.0
  }

  return { positions, nodePositions: nodes }
}

/**
 * Get edges for drawing network connections.
 * Connects nodes that are within a distance threshold.
 */
export function getNetworkEdges(nodes: NetworkNode[], threshold = 0.65): [number, number][] {
  const edges: [number, number][] = []

  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[i].x - nodes[j].x
      const dy = nodes[i].y - nodes[j].y
      const dist = Math.sqrt(dx * dx + dy * dy)
      if (dist < threshold) {
        edges.push([i, j])
      }
    }
  }

  return edges
}
